<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 条件搜索 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="6" :sm="24">
              <a-form-item label="平台商品编号" prop="id">
                <a-input v-model="queryParam.id" @pressEnter="handleQuery" :maxLength="20" placeholder="请输入平台商品编号" allow-clear/>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="商品名称" prop="goodsName">
                <a-input v-model="queryParam.goodsName" @pressEnter="handleQuery" placeholder="请输入商品名称" allow-clear/>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="平台商品分类" prop="categoryId">
                <a-tree-select
                  v-model="queryParam.categoryId"
                  style="width: 100%"
                  :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
                  :tree-data="cateOptions"
                  :replaceFields="{ children: 'children', title: 'categoryName', key: 'id', value: 'id' }"
                  placeholder="请选择商品分类"
                  tree-default-expand-all
                  allow-clear>
                </a-tree-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />查询</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />重置</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <a-alert type="info" show-icon style="margin-bottom: 16px">
        <template slot="message">
          平台上传的商品均可在本店报价。平台只定义规格名，本店填写规格值、售价、库存与运费后提交审核；审核通过后买家选中对应规格即可看到本店报价并直接购买或加入购物车。
        </template>
      </a-alert>
      <!-- 数据展示 -->
      <a-table
        :scroll="{ x: 1200 }"
        :loading="loading"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :pagination="false"
        :bordered="false"
      >
        <template slot="picture" slot-scope="text, record">
          <img v-if="record.picture" :src="record.picture" style="width:60px;height:62px;" alt="none"/>
        </template>
        <span slot="categoryNameList" slot-scope="text, record">
          <template v-for="categoryName in record.categoryNameList">
            {{ categoryName }}
          </template>
        </span>
        <span slot="quoteStatus" slot-scope="text, record">
          <custom-dict-tag
            v-if="record.quoteStatus !== null && record.quoteStatus !== undefined"
            :options="customDict.GoodsStatusEnum"
            :value="record.quoteStatus"/>
          <a-tag v-else color="default">未报价</a-tag>
        </span>
        <span slot="quotePrice" slot-scope="text, record">
          <span v-if="record.quotePrice !== null && record.quotePrice !== undefined">￥{{ record.quotePrice }}</span>
          <span v-else>-</span>
        </span>
        <span slot="onlineBuy" slot-scope="text, record">
          <a-tag v-if="record.quoteStatus === 3 && record.quoteStock > 0" color="green">可购买</a-tag>
          <a-tooltip v-else-if="record.quoteStatus === 3" title="本店报价库存为0，买家暂时无法购买">
            <a-tag color="orange">无库存</a-tag>
          </a-tooltip>
          <a-tooltip v-else-if="record.quoteStatus !== null && record.quoteStatus !== undefined" title="报价待平台审核通过后买家才可购买">
            <a-tag color="orange">仅浏览</a-tag>
          </a-tooltip>
          <span v-else>-</span>
        </span>
        <span slot="operation" slot-scope="text, record">
          <a-button size="small" type="link" @click="handleQuote(record)" v-hasPermi="['goods:platform:apply']">
            <a-icon type="edit" />{{ record.quoteNum ? '修改本店报价' : '本店报价' }}
          </a-button>
          <a-button
            size="small"
            type="link"
            style="color:#f5222d"
            v-if="record.quoteNum"
            @click="handleCancel(record)"
            v-hasPermi="['goods:platform:apply']">
            <a-icon type="rollback" />撤销报价
          </a-button>
        </span>
      </a-table>
      <!-- 分页 -->
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :pageSizeOptions="pageSizeOptions"
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="total => `共 ${total} 条`"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
    <quote-form ref="quoteForm" @ok="getList"/>
  </page-header-wrapper>
</template>

<script>
import { pagePlatformPool, cancelPlatformQuote } from '@/api/goods/goods'
import { platformCategoryTree } from '@/api/goods/category'
import CustomDictTag from '@/components/DictCustomTag'
import { mapGetters } from 'vuex'
import QuoteForm from './modules/QuoteForm'

export default {
  name: 'PlatformGoods',
  components: {
    CustomDictTag,
    QuoteForm
  },
  data () {
    return {
      loading: false,
      list: [],
      total: 0,
      cateOptions: [],
      pageSizeOptions: ['10', '20', '50', '100'],
      queryParam: {
        id: undefined,
        goodsName: undefined,
        categoryId: undefined,
        pageNum: 1,
        pageSize: 10
      },
      columns: [
        { title: '平台商品编号', dataIndex: 'id', width: 160 },
        { title: '商品名称', dataIndex: 'goodsName', width: 300 },
        { title: '商品主图', dataIndex: 'picture', width: '80px', scopedSlots: { customRender: 'picture' } },
        { title: '平台商品分类', dataIndex: 'categoryNameList', width: '140px', scopedSlots: { customRender: 'categoryNameList' } },
        { title: '本店最低报价', dataIndex: 'quotePrice', width: '110px', scopedSlots: { customRender: 'quotePrice' } },
        { title: '本店报价状态', dataIndex: 'quoteStatus', width: '110px', scopedSlots: { customRender: 'quoteStatus' } },
        { title: '是否可线上购买', dataIndex: 'onlineBuy', width: '130px', scopedSlots: { customRender: 'onlineBuy' } },
        { title: '操作', dataIndex: 'operation', width: 200, scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    ...mapGetters(['customDict'])
  },
  created () {
    this.getList()
    platformCategoryTree({}).then(response => {
      this.cateOptions = response.data
    })
  },
  methods: {
    getList () {
      this.loading = true
      pagePlatformPool(this.queryParam).then(response => {
        this.list = response.data.records || []
        this.total = response.data.total
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.queryParam = {
        id: undefined,
        goodsName: undefined,
        categoryId: undefined,
        pageNum: 1,
        pageSize: this.queryParam.pageSize
      }
      this.getList()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.queryParam.pageNum = 1
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    handleQuote (record) {
      this.$refs.quoteForm.handleOpen(record.id)
    },
    handleCancel (record) {
      const that = this
      this.$confirm({
        title: '撤销本店报价',
        content: '确认撤销该商品在本店的全部报价吗？撤销后买家将无法购买本店该商品。',
        onOk () {
          return cancelPlatformQuote({ id: record.id }).then(() => {
            that.$message.success('撤销成功', 3)
            that.getList()
          })
        }
      })
    }
  }
}
</script>
